import { Injectable } from "@angular/core";
import { AngularFireDatabase } from "angularfire2/database";
import { Observable } from "rxjs/Observable";
import { UserService } from "../../user/user.service";
import { BackendService } from "../../services/backend.service";
import { Property } from "../../property/property";
import { Community } from "../../../shared/services/community/community";
import { Unit } from "../../unit/unit";
import { User } from "../../user/user";
import { CountryService } from "../../../shared/location/country.service";
import { StateService } from "../../../shared/location/state.service";
import { CityService } from "../../../shared/location/city.service";
import { AuthService } from "../../../shared/services/auth.service";

@Injectable()
export class ResidentService {
  property: Property;
  community: Community;
  unit: Unit;

  constructor(
    private db: AngularFireDatabase, private userService: UserService, private countryService: CountryService,
    private stateService: StateService, private cityService: CityService, private authService: AuthService
  ) { }

  getResidents(unitId: string, communityId: string = ""): any {
    return this.db
      .object(`/residents/${unitId}`)
      .valueChanges()
      .map(data => {
        const residentIds = data ? Object.keys(data) : [];

        return residentIds.map(id => {
          const resident$ = this.db.object(`/profile/${id}`).valueChanges();

          return Observable.zip(resident$, (profile: any) => {
            profile.name = `${profile.firstName} ${profile.lastName}`;
            if (communityId && profile.communities) {
              const community = profile.communities[communityId];
              if (community && community.roles) {
                const roles = Object.keys(community.roles);
                profile["isActive"] = roles.includes("Resident");
              } else {
                profile["isActive"] = false;
              }
            } else {
              profile["isActive"] = false;
            }

            return profile;
          });
        });
      })
      .flatMap(user => {
        return user.length ? Observable.combineLatest(user) : Observable.of([]);
      });
  }

  async store(community, unitId, user): Promise<any> {
    const communityId = community["id"];
    const userExists = await this.doesUserExistAsResident(unitId, user.id);
    if (userExists) {
      return { state: false, message: "User already exists as a Resident. Verify user\"s email" };
    } else {
      delete user.isAdmin;
      delete user.isSuper;

      Promise.all([
        this.db.list(`/profile`).update(user.id, user),
        this.db.list(`/profile/${user.id}/communities`).update(communityId, { id: communityId, name: community["name"] }),
        this.db.list(`/profile/${user.id}/communities/${communityId}`).update("roles", { Resident: true }),
        this.db.list(`/residents/${unitId}`).set(user.id, true),
        this.db.list(`/community_users/${communityId}`).set(user.id, true)
      ]).then();

      return { state: true, message: "User successfully added." };
    }
  }

  deactivate(communityId, userId): any {
    this.userService.updateUserRoleStatus(userId, "Resident", false, communityId);
  }

  activate(communityId, userId): any {
    this.userService.updateUserRoleStatus(userId, "Resident", true, communityId);
  }

  private async doesUserExistAsResident(unitId, userId): Promise<any> {
    return new Promise<any>(resolve => {
      this.db
        .object(`/residents/${unitId}/${userId}`)
        .valueChanges()
        .subscribe(data => resolve(data));
    });
  }

  async update(community, resident: User): Promise<any> {
    const result = { state: true, message: "User successfully updated." };
    try {
      delete resident["communityId"];
      delete resident.isAdmin;
      delete resident.isSuper;

      this.db.list(`/profile`).update(resident.id, resident).then(() => {
        this.db.list(`/profile/${resident.id}/communities`).update(community.id, { id: community.id, name: community.name });
      });
    } catch (err) {
      result.state = false;
      result.message = "User operation failed, plesae try again later.";
    }

    return result;
  }

  import(data, community, operatorId): any {
    const keys = data.shift();
    let profiles = [];
    data.forEach(values => {
      const profile = {};
      keys.forEach((key, index) => {
        const email = values[keys.indexOf("email")];
        profile[key] = values[index] ? values[index].trim() : "";
        profile["communityId"] = community.id;
        profile["createdAt"] = Date.now();
        profile["operatorId"] = operatorId;
        profile["id"] = email ? BackendService.generateUserId(email) : "";
      });
      profiles.push(profile);
      profiles = profiles
        .filter(resident => resident.firstName && resident.lastName && resident.email && resident.gender);
    });
    profiles.forEach(profile => {
      this.authService.authenticateNewUser(profile.email).then(
        response => {
          if (response.state) {
            this.store(community, profile.unitId, profile);
          }
        }
      ).catch(err => console.log("error :: ", err));
    });
  }

  async composeCommunityAddress(address): Promise<string> {
    return new Promise<string>(resolve => {
      if (address) {
        const countryName = this.countryService.getCountryObjByCode(address.countryCode).name.common;
        this.stateService.getStateById(address.countryCode, address.stateId)
          .subscribe(state => {
            this.cityService.getCityById(address.countryCode, address.stateId, address.lgaId)
              .subscribe(city => {
                const composedAddress = `${address.street}, ${city}, ${state}, ${countryName}`;
                resolve(composedAddress);
              });
          });
      } else {
        resolve("");
      }
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/user/resident.service.ts